/**
 * TaskAPI 配置接口
 *
 * 用于配置 TaskAPI 的端点和默认参数，支持不同项目使用不同的端点
 */

/**
 * TaskAPI 端点配置
 */
export interface TaskAPIEndpoints {
  /** 获取任务列表 */
  list: string
  /** 提交任务 */
  submit: string
  /** 获取任务状态 */
  status: string
  /** 删除任务 */
  delete: string
  /** 获取广场列表（可选） */
  squareList?: string
  /** 推荐到广场（可选） */
  recommend?: string
  /** 取消推荐（可选） */
  cancelRecommend?: string
}

/**
 * TaskAPI 配置
 */
export interface TaskAPIConfig {
  /** 端点配置 */
  endpoints: TaskAPIEndpoints
  /** 默认参数（可选） */
  defaultParams?: {
    /** 默认的 mode_types 参数 */
    modeTypes?: string
    /** 其他默认参数 */
    [key: string]: unknown
  }
}

/**
 * 默认的 TaskAPI 配置
 *
 * 注意：这是通用的占位配置，实际使用时应通过 SDK 配置覆盖
 * 每个项目应该在初始化时提供自己的端点配置
 */
export const DEFAULT_TASK_API_CONFIG: TaskAPIConfig = {
  endpoints: {
    list: '/api/task/list',
    submit: '/api/task/submit',
    status: '/api/task/{serialNo}',
    delete: '/api/task/{serialNo}',
    squareList: '/api/square/list',
    recommend: '/api/square/recommend',
    cancelRecommend: '/api/square/recommend/{taskId}'
  },
  defaultParams: {}
}

/**
 * 验证 TaskAPI 配置
 *
 * @param config - 配置对象
 * @throws {Error} 如果配置无效
 */
export function validateTaskAPIConfig(config: TaskAPIConfig): void {
  if (!config) {
    throw new Error('TaskAPI 配置不能为空')
  }

  if (!config.endpoints) {
    throw new Error('TaskAPI 端点配置不能为空')
  }

  // 验证必需的端点
  const requiredEndpoints: (keyof TaskAPIEndpoints)[] = [
    'list',
    'submit',
    'status',
    'delete'
  ]

  for (const endpoint of requiredEndpoints) {
    if (!config.endpoints[endpoint]) {
      throw new Error(`TaskAPI 端点 "${endpoint}" 是必需的`)
    }
  }
}

/**
 * 合并 TaskAPI 配置
 *
 * @param userConfig - 用户配置
 * @param defaultConfig - 默认配置
 * @returns 合并后的配置
 */
export function mergeTaskAPIConfig(
  userConfig?: Partial<TaskAPIConfig>,
  defaultConfig: TaskAPIConfig = DEFAULT_TASK_API_CONFIG
): TaskAPIConfig {
  if (!userConfig) {
    return defaultConfig
  }

  // 合并端点配置，用户配置优先
  const mergedEndpoints = {
    ...defaultConfig.endpoints,
    ...userConfig.endpoints
  }

  // 合并默认参数
  const mergedDefaultParams = {
    ...defaultConfig.defaultParams,
    ...userConfig.defaultParams
  }

  return {
    endpoints: mergedEndpoints as TaskAPIEndpoints,
    defaultParams: mergedDefaultParams
  }
}

/**
 * 替换端点中的占位符
 *
 * @param endpoint - 端点模板（如 "/api/task/{id}"）
 * @param params - 参数对象
 * @returns 替换后的端点
 *
 * @example
 * replaceEndpointParams('/api/task/{id}', { id: '123' })
 * // 返回: '/api/task/123'
 */
export function replaceEndpointParams(
  endpoint: string,
  params: Record<string, string | number>
): string {
  let result = endpoint

  for (const [key, value] of Object.entries(params)) {
    result = result.replace(`{${key}}`, String(value))
  }

  return result
}
